import { CellType, CellValueDetailedType, SimpleCellAddress, type SimpleCellRange } from 'hyperformula';
import { prop } from 'lodash/fp';

import {
    CellTypeNames,
    CellTypeObjectNames,
    DateStringFormatOptions,
    FormatOptions,
    TimeStringFormatOptions,
} from '../../../../../../common/table/CellTypeConstants';
import {
    type CellData,
    type CellDataChange,
    CellTypeObject,
    TableChangeSource,
} from '../../../../../../common/table/TableTypes';
import { isCellOfType } from '../../../../../../common/table/utils/tableCellDataPropertyUtils';
import { getDefaultTypeObject } from '../../../../../../common/table/utils/tableCellTypeUtils';
import hyperFormulaInstance from '../../../manager/hyperFormulaInstance';
import { cloneDeep, isEmpty, omit } from 'lodash';
import { normaliseComputedCellValue } from '../../dataUtils/cell/tableCellRenderValueUtils';
import { convertCellDataToReduxFormat } from '../../dataUtils/persistence/tablePersistenceValueUtils';
import { MNHotInstance } from '../../../tableHotInstanceTypes';

const FORMULAS_INFER_FIRST_ARG_CELL_TYPE = [
    'SUM',
    'SUMIF',
    'SUMIFS',
    'SUMSQ',
    'SUMX2MY2',
    'SUMX2PY2',
    'SUMXMY2',
    'SUMPRODUCT',
    'MIN',
    'MINA',
    'MINIFS',
    'MAX',
    'MAXA',
    'MAXIFS',
    'AVERAGE',
    'AVERAGEA',
    'AVERAGEIF',
];

const CELL_FORMULA_REGEX = /=([A-Za-z0-9]*)\(.*\)/;

type FormulaCellDataChanges = Partial<Pick<CellData, 'type' | 'resultValue'>>;

/**
 * Infers the Milanote cell type for a formula cell based on HyperFormula metadata and precedents.
 */
export const inferFormulaCellType = (
    cellAddress: SimpleCellAddress,
    hotInstance: MNHotInstance,
): CellTypeObject | undefined => {
    const cellFormula = hyperFormulaInstance.getCellFormula(cellAddress);
    if (!cellFormula) return;

    const getCellTypeFromCellMeta = (cellType: CellTypeObjectNames) => {
        const { cellData } = hotInstance.getCellMeta(cellAddress.row, cellAddress.col) || {};
        return (isCellOfType(cellType)(cellData) && cellData?.type) || getDefaultTypeObject(cellType);
    };

    const hfValueDetailedType = hyperFormulaInstance.getCellValueDetailedType(cellAddress);
    switch (hfValueDetailedType) {
        case CellValueDetailedType.NUMBER_DATE:
            return {
                ...getCellTypeFromCellMeta(CellTypeNames.DATE_TIME),
                [FormatOptions.HAS_TIME]: false,
            };
        case CellValueDetailedType.NUMBER_DATETIME:
            return {
                ...getCellTypeFromCellMeta(CellTypeNames.DATE_TIME),
                [FormatOptions.HAS_TIME]: true,
                [FormatOptions.TIME_FORMAT]: TimeStringFormatOptions.TWELVE_HOUR,
            };
        case CellValueDetailedType.NUMBER_TIME:
            return {
                ...getCellTypeFromCellMeta(CellTypeNames.DATE_TIME),
                [FormatOptions.HAS_TIME]: true,
                [FormatOptions.TIME_FORMAT]: TimeStringFormatOptions.TWELVE_HOUR_SECONDS,
                [FormatOptions.TIME_IN_BRACKETS]: false,
                [FormatOptions.DATE_FORMAT]: DateStringFormatOptions.NONE,
            };
        case CellValueDetailedType.NUMBER_PERCENT:
            return getCellTypeFromCellMeta(CellTypeNames.PERCENTAGE);
        case CellValueDetailedType.NUMBER_CURRENCY:
            return getCellTypeFromCellMeta(CellTypeNames.CURRENCY);
        default: {
            const cellFormulaFnName = prop(1, cellFormula.match(CELL_FORMULA_REGEX))?.toUpperCase();
            const shouldInferBasedOnFirstPrecedentCell =
                cellFormulaFnName && FORMULAS_INFER_FIRST_ARG_CELL_TYPE.includes(cellFormulaFnName);

            if (!shouldInferBasedOnFirstPrecedentCell) return;

            let firstPrecedentCellAddress = prop(0, hyperFormulaInstance.getCellPrecedents(cellAddress));

            if (firstPrecedentCellAddress && 'start' in firstPrecedentCellAddress) {
                firstPrecedentCellAddress = firstPrecedentCellAddress.start;
            }

            return firstPrecedentCellAddress && inferFormulaCellType(firstPrecedentCellAddress, hotInstance);
        }
    }
};

const createFormulaCellDataChange = ({
    cellAddress,
    hotInstance,
    data,
    locale,
}: {
    cellAddress: SimpleCellAddress;
    hotInstance: MNHotInstance;
    data: CellData[][];
    locale: string;
}): CellDataChange | null => {
    const { row, col } = cellAddress;

    const nextCellDataChanges: FormulaCellDataChanges = {};
    const previousCellData = cloneDeep(data[row][col]);
    const tempCellData = cloneDeep(previousCellData);

    const currentType = tempCellData.type;
    const inferredType = inferFormulaCellType(cellAddress, hotInstance);
    if (inferredType && inferredType.name !== currentType?.name) {
        nextCellDataChanges.type = inferredType;
        tempCellData.type = inferredType;
    }

    if (hyperFormulaInstance.getCellType(cellAddress) === CellType.FORMULA) {
        const hotComputedCellValue = hyperFormulaInstance.getCellValue(cellAddress) as Parameters<
            typeof normaliseComputedCellValue
        >[0];

        tempCellData.value = normaliseComputedCellValue(hotComputedCellValue, locale, {
            hfType: hyperFormulaInstance.getCellType(cellAddress),
            hfDetailedType: hyperFormulaInstance.getCellValueDetailedType(cellAddress),
        }) as CellData['value'];

        const currentResultValue = tempCellData.resultValue;
        const resultValue = convertCellDataToReduxFormat(tempCellData, locale).value;

        if (resultValue !== undefined && resultValue !== currentResultValue) {
            nextCellDataChanges.resultValue = resultValue;
        }
    }

    if (isEmpty(nextCellDataChanges)) return null;

    const newCellData = {
        ...omit(previousCellData, ['resultValue']),
        ...nextCellDataChanges,
    };

    return [row, col, previousCellData, newCellData];
};

export const recursivelyInferTypeForFormulaCell = ({
    cellAddress,
    level,
    tableSource,
    hotInstance,
    data,
    locale,
    visited,
}: {
    cellAddress: SimpleCellAddress | SimpleCellRange;
    level: number;
    tableSource: TableChangeSource | undefined;
    hotInstance: MNHotInstance;
    data: CellData[][];
    locale: string;
    visited: WeakMap<object, boolean>;
}): CellDataChange[] => {
    if (visited.get(cellAddress)) return [];
    visited.set(cellAddress, true);

    const cellDataChanges: CellDataChange[] = [];

    // These sources should NOT trigger the formula type re-inference for the current cell, but may for dependent cells
    const isUpdateCellTypeChange =
        tableSource === TableChangeSource.CELL_META_UPDATE_CELL_TYPE ||
        tableSource === TableChangeSource.CELL_META_UPDATE_CELL_TYPE_FORMAT;

    const shouldSkipInferenceForRootCell = level === 0 && isUpdateCellTypeChange;
    const isCellAddress = 'row' in cellAddress && 'col' in cellAddress;

    if (!shouldSkipInferenceForRootCell && isCellAddress) {
        const formulaCellDataChange = createFormulaCellDataChange({
            cellAddress,
            hotInstance,
            data,
            locale,
        });

        if (formulaCellDataChange) {
            cellDataChanges.push(formulaCellDataChange);
        }
    }

    const cellDependents = hyperFormulaInstance.getCellDependents(cellAddress);
    cellDependents.forEach((cellDependent) => {
        cellDataChanges.push(
            ...recursivelyInferTypeForFormulaCell({
                cellAddress: cellDependent,
                level: level + 1,
                tableSource,
                hotInstance,
                data,
                locale,
                visited,
            }),
        );
    });

    return cellDataChanges;
};
